import { noop } from "lodash";
import { useMemo } from "react";

import { browserLocalStorage } from "@/react/lib/browserStorage";

interface UseLocalStorage<T> {
  getValue: () => T | undefined | null;
  resetValue: () => void;
  setValue: (value: T) => void;
}

export const useLocalStorage = <T>(key?: string): UseLocalStorage<T> =>
  useMemo(() => {
    if (!key) {
      return {
        getValue: () => undefined,
        resetValue: noop,
        setValue: noop,
      };
    }

    return {
      getValue: () => {
        try {
          return safeParseJSON<T>(browserLocalStorage.getItem(key));
        } catch (error) {
          console.error("LocalStorage is not accessible", error);
          return null;
        }
      },
      setValue: (value: T) => {
        try {
          return browserLocalStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.error("LocalStorage is not accessible", error);
        }
      },
      resetValue: () => {
        try {
          return browserLocalStorage.removeItem(key);
        } catch (error) {
          console.error("LocalStorage is not accessible", error);
        }
      },
    } as const;
  }, [key]);

const safeParseJSON = <T>(value: string | null): T | null | undefined => {
  try {
    if (value === null) {
      return null;
    }

    return JSON.parse(value) as T;
  } catch (error) {
    console.error("Parsing JSON failed", error);
    return undefined;
  }
};
